import React, { useState, useEffect } from 'react';
import { Plus, Search, Building2, Phone, Mail, MapPin, Star, MoreVertical, Edit, Trash2, Eye, Truck, DollarSign, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier, rateSupplier, getCompanies } from '../lib/api';

const ASSET_TYPES = [
  'Pickup', 'Truck', 'Bus', 'Excavator', 'Bulldozer', 'Crane', 'Loader', 
  'Forklift', 'Trailer', 'Tanker', 'Compactor', 'Grader', 'Roller'
];

const PAYMENT_TERMS = ['Immediate', '15 days', '30 days', '45 days', '60 days', '90 days'];

const emptySupplier = {
  company_id: '',
  name: '',
  trade_name: '',
  registration_number: '',
  vat_number: '',
  address: '',
  city: '',
  country: 'Saudi Arabia',
  contacts: [],
  assets: [],
  bank_details: null,
  payment_terms: '30 days',
  credit_limit: null,
  notes: ''
};

const emptyContact = { name: '', position: '', email: '', phone: '', is_primary: false };
const emptyAsset = { asset_type: '', quantity: 1, daily_rate: 0, monthly_rate: 0, with_operator: false, operator_rate: 0 };
const emptyBankDetails = { bank_name: '', account_name: '', account_number: '', iban: '', swift_code: '' };

export const SuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isRatingDialogOpen, setIsRatingDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [formData, setFormData] = useState(emptySupplier);
  const [activeTab, setActiveTab] = useState('basic');
  const [newRating, setNewRating] = useState(0);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, companyFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [suppliersRes, companiesRes] = await Promise.all([
        getSuppliers(
          companyFilter && companyFilter !== 'all' ? companyFilter : null, 
          statusFilter && statusFilter !== 'all' ? statusFilter : null, 
          searchTerm || null
        ),
        getCompanies()
      ]);
      setSuppliers(suppliersRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error('Failed to load suppliers');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedSupplier(null);
    setFormData({ ...emptySupplier, company_id: companies[0]?.id || '' });
    setActiveTab('basic');
    setIsDialogOpen(true);
  };

  const handleEdit = (supplier) => {
    setSelectedSupplier(supplier);
    setFormData({
      ...supplier,
      contacts: supplier.contacts || [],
      assets: supplier.assets || [],
      bank_details: supplier.bank_details || null
    });
    setActiveTab('basic');
    setIsDialogOpen(true);
  };

  const handleView = (supplier) => {
    setSelectedSupplier(supplier);
    setIsViewDialogOpen(true);
  };

  const handleRateDialog = (supplier) => {
    setSelectedSupplier(supplier);
    setNewRating(supplier.performance_rating || 0);
    setIsRatingDialogOpen(true);
  };

  const handleDelete = async (supplier) => {
    if (window.confirm(`Are you sure you want to deactivate ${supplier.name}?`)) {
      try {
        await deleteSupplier(supplier.id);
        toast.success('Supplier deactivated');
        loadData();
      } catch (error) {
        toast.error('Failed to delete supplier');
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.company_id) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (selectedSupplier) {
        await updateSupplier(selectedSupplier.id, formData);
        toast.success('Supplier updated');
      } else {
        await createSupplier(formData);
        toast.success('Supplier created');
      }
      setIsDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save supplier');
      console.error(error);
    }
  };

  const handleRate = async () => {
    try {
      await rateSupplier(selectedSupplier.id, newRating);
      toast.success(`Rated ${selectedSupplier.name}: ${newRating}/5`);
      setIsRatingDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to rate supplier');
    }
  };

  // Contact management
  const addContact = () => {
    setFormData({ ...formData, contacts: [...formData.contacts, { ...emptyContact }] });
  };
  const removeContact = (index) => {
    setFormData({ ...formData, contacts: formData.contacts.filter((_, i) => i !== index) });
  };
  const updateContact = (index, field, value) => {
    const updated = [...formData.contacts];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, contacts: updated });
  };

  // Asset management
  const addAsset = () => {
    setFormData({ ...formData, assets: [...formData.assets, { ...emptyAsset }] });
  };
  const removeAsset = (index) => {
    setFormData({ ...formData, assets: formData.assets.filter((_, i) => i !== index) });
  };
  const updateAsset = (index, field, value) => {
    const updated = [...formData.assets];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, assets: updated });
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      blacklisted: 'bg-red-100 text-red-800'
    };
    return <Badge className={variants[status] || variants.inactive}>{status}</Badge>;
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${star <= (rating || 0) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || 'Unknown';
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Suppliers</h1>
          <p className="text-muted-foreground mt-1">Manage your external suppliers and rental partners</p>
        </div>
        <Button onClick={handleCreate} data-testid="add-supplier-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Supplier
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-suppliers"
              />
            </div>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="w-[200px]" data-testid="company-filter">
                <SelectValue placeholder="All Companies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="blacklisted">Blacklisted</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Building2 className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{suppliers.length}</p>
                <p className="text-sm text-muted-foreground">Total Suppliers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Truck className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {suppliers.reduce((acc, s) => acc + (s.assets?.length || 0), 0)}
                </p>
                <p className="text-sm text-muted-foreground">Asset Types</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Star className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {(suppliers.filter(s => s.performance_rating).reduce((acc, s) => acc + s.performance_rating, 0) / 
                    (suppliers.filter(s => s.performance_rating).length || 1)).toFixed(1)}
                </p>
                <p className="text-sm text-muted-foreground">Avg Rating</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {suppliers.reduce((acc, s) => acc + (s.total_spent || 0), 0).toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">Total Spent (SAR)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Suppliers List */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading suppliers...</div>
      ) : suppliers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">No suppliers found</h3>
            <p className="text-muted-foreground mt-1">Add your first supplier to get started</p>
            <Button className="mt-4" onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Add Supplier
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {suppliers.map((supplier) => (
            <Card key={supplier.id} className="hover:shadow-md transition-shadow" data-testid={`supplier-card-${supplier.id}`}>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold">{supplier.name}</h3>
                      {getStatusBadge(supplier.status)}
                    </div>
                    {supplier.trade_name && (
                      <p className="text-sm text-muted-foreground mb-2">{supplier.trade_name}</p>
                    )}
                    
                    <div className="space-y-1 text-sm">
                      {supplier.city && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPin className="h-4 w-4" />
                          {supplier.city}, {supplier.country}
                        </div>
                      )}
                      {supplier.contacts?.[0] && (
                        <>
                          {supplier.contacts[0].email && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Mail className="h-4 w-4" />
                              {supplier.contacts[0].email}
                            </div>
                          )}
                          {supplier.contacts[0].phone && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Phone className="h-4 w-4" />
                              {supplier.contacts[0].phone}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    <div className="flex items-center gap-4 mt-3">
                      {renderStars(supplier.performance_rating)}
                      <span className="text-xs text-muted-foreground">
                        {supplier.total_orders} orders | {supplier.payment_terms}
                      </span>
                    </div>

                    {supplier.assets && supplier.assets.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {supplier.assets.slice(0, 4).map((asset, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {asset.asset_type} ({asset.quantity})
                          </Badge>
                        ))}
                        {supplier.assets.length > 4 && (
                          <Badge variant="outline" className="text-xs">
                            +{supplier.assets.length - 4} more
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`supplier-menu-${supplier.id}`}>
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleView(supplier)}>
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleEdit(supplier)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleRateDialog(supplier)}>
                        <Star className="h-4 w-4 mr-2" />
                        Rate Supplier
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(supplier)} className="text-red-600">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Deactivate
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedSupplier ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="contacts">Contacts</TabsTrigger>
              <TabsTrigger value="assets">Assets</TabsTrigger>
              <TabsTrigger value="bank">Bank Details</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company *</Label>
                  <Select value={formData.company_id} onValueChange={(v) => setFormData({ ...formData, company_id: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Supplier Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter supplier name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Trade Name</Label>
                  <Input
                    value={formData.trade_name || ''}
                    onChange={(e) => setFormData({ ...formData, trade_name: e.target.value })}
                    placeholder="Trading as..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Registration Number</Label>
                  <Input
                    value={formData.registration_number || ''}
                    onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                    placeholder="CR Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>VAT Number</Label>
                  <Input
                    value={formData.vat_number || ''}
                    onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                    placeholder="VAT Registration"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Terms</Label>
                  <Select value={formData.payment_terms} onValueChange={(v) => setFormData({ ...formData, payment_terms: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS.map((term) => (
                        <SelectItem key={term} value={term}>{term}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Credit Limit (SAR)</Label>
                  <Input
                    type="number"
                    value={formData.credit_limit || ''}
                    onChange={(e) => setFormData({ ...formData, credit_limit: parseFloat(e.target.value) || null })}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Select value={formData.country || 'Saudi Arabia'} onValueChange={(v) => setFormData({ ...formData, country: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Saudi Arabia">Saudi Arabia</SelectItem>
                      <SelectItem value="UAE">UAE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={formData.city || ''}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="City"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Address</Label>
                  <Textarea
                    value={formData.address || ''}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Full address"
                    rows={2}
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={2}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="contacts" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <Label className="text-base">Contact Persons</Label>
                <Button type="button" variant="outline" size="sm" onClick={addContact}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Contact
                </Button>
              </div>
              
              {formData.contacts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No contacts added. Click &quot;Add Contact&quot; to add one.
                </div>
              ) : (
                formData.contacts.map((contact, index) => (
                  <Card key={index}>
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start mb-3">
                        <Label className="text-sm font-medium">Contact #{index + 1}</Label>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeContact(index)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          placeholder="Name *"
                          value={contact.name}
                          onChange={(e) => updateContact(index, 'name', e.target.value)}
                        />
                        <Input
                          placeholder="Position"
                          value={contact.position || ''}
                          onChange={(e) => updateContact(index, 'position', e.target.value)}
                        />
                        <Input
                          placeholder="Email"
                          type="email"
                          value={contact.email || ''}
                          onChange={(e) => updateContact(index, 'email', e.target.value)}
                        />
                        <Input
                          placeholder="Phone"
                          value={contact.phone || ''}
                          onChange={(e) => updateContact(index, 'phone', e.target.value)}
                        />
                        <div className="col-span-2 flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`primary-${index}`}
                            checked={contact.is_primary}
                            onChange={(e) => updateContact(index, 'is_primary', e.target.checked)}
                            className="rounded"
                          />
                          <label htmlFor={`primary-${index}`} className="text-sm">Primary Contact</label>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="assets" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <Label className="text-base">Available Assets</Label>
                <Button type="button" variant="outline" size="sm" onClick={addAsset}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Asset Type
                </Button>
              </div>
              
              {formData.assets.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No assets added. Click &quot;Add Asset Type&quot; to add available equipment.
                </div>
              ) : (
                formData.assets.map((asset, index) => (
                  <Card key={index}>
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start mb-3">
                        <Label className="text-sm font-medium">Asset #{index + 1}</Label>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeAsset(index)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <Select 
                          value={asset.asset_type} 
                          onValueChange={(v) => updateAsset(index, 'asset_type', v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Asset Type *" />
                          </SelectTrigger>
                          <SelectContent>
                            {ASSET_TYPES.map((type) => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          placeholder="Quantity"
                          value={asset.quantity}
                          onChange={(e) => updateAsset(index, 'quantity', parseInt(e.target.value) || 0)}
                        />
                        <Input
                          type="number"
                          placeholder="Daily Rate (SAR)"
                          value={asset.daily_rate}
                          onChange={(e) => updateAsset(index, 'daily_rate', parseFloat(e.target.value) || 0)}
                        />
                        <Input
                          type="number"
                          placeholder="Monthly Rate (SAR)"
                          value={asset.monthly_rate}
                          onChange={(e) => updateAsset(index, 'monthly_rate', parseFloat(e.target.value) || 0)}
                        />
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`operator-${index}`}
                            checked={asset.with_operator}
                            onChange={(e) => updateAsset(index, 'with_operator', e.target.checked)}
                            className="rounded"
                          />
                          <label htmlFor={`operator-${index}`} className="text-sm">With Operator</label>
                        </div>
                        {asset.with_operator && (
                          <Input
                            type="number"
                            placeholder="Operator Rate/day"
                            value={asset.operator_rate || ''}
                            onChange={(e) => updateAsset(index, 'operator_rate', parseFloat(e.target.value) || 0)}
                          />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="bank" className="space-y-4 mt-4">
              <Label className="text-base">Bank Account Details</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Input
                    value={formData.bank_details?.bank_name || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      bank_details: { ...(formData.bank_details || emptyBankDetails), bank_name: e.target.value }
                    })}
                    placeholder="Bank Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Account Name</Label>
                  <Input
                    value={formData.bank_details?.account_name || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      bank_details: { ...(formData.bank_details || emptyBankDetails), account_name: e.target.value }
                    })}
                    placeholder="Account Holder Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Account Number</Label>
                  <Input
                    value={formData.bank_details?.account_number || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      bank_details: { ...(formData.bank_details || emptyBankDetails), account_number: e.target.value }
                    })}
                    placeholder="Account Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>IBAN</Label>
                  <Input
                    value={formData.bank_details?.iban || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      bank_details: { ...(formData.bank_details || emptyBankDetails), iban: e.target.value }
                    })}
                    placeholder="SA..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>SWIFT Code</Label>
                  <Input
                    value={formData.bank_details?.swift_code || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      bank_details: { ...(formData.bank_details || emptyBankDetails), swift_code: e.target.value }
                    })}
                    placeholder="SWIFT/BIC"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-supplier-btn">
              {selectedSupplier ? 'Update Supplier' : 'Create Supplier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Supplier Details</DialogTitle>
          </DialogHeader>
          
          {selectedSupplier && (
            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-bold">{selectedSupplier.name}</h3>
                  {selectedSupplier.trade_name && (
                    <p className="text-muted-foreground">{selectedSupplier.trade_name}</p>
                  )}
                </div>
                {getStatusBadge(selectedSupplier.status)}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium text-muted-foreground">Company</p>
                  <p>{getCompanyName(selectedSupplier.company_id)}</p>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">Registration #</p>
                  <p>{selectedSupplier.registration_number || '-'}</p>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">VAT Number</p>
                  <p>{selectedSupplier.vat_number || '-'}</p>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">Payment Terms</p>
                  <p>{selectedSupplier.payment_terms}</p>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">Location</p>
                  <p>{selectedSupplier.city}, {selectedSupplier.country}</p>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">Credit Limit</p>
                  <p>{selectedSupplier.credit_limit ? `SAR ${selectedSupplier.credit_limit.toLocaleString()}` : '-'}</p>
                </div>
              </div>

              {selectedSupplier.contacts?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Contacts</h4>
                  <div className="space-y-2">
                    {selectedSupplier.contacts.map((c, i) => (
                      <div key={i} className="p-3 bg-muted rounded-lg text-sm">
                        <p className="font-medium">{c.name} {c.is_primary && <Badge className="ml-2" variant="secondary">Primary</Badge>}</p>
                        {c.position && <p className="text-muted-foreground">{c.position}</p>}
                        <div className="flex gap-4 mt-1">
                          {c.email && <span>{c.email}</span>}
                          {c.phone && <span>{c.phone}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedSupplier.assets?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Available Assets</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Type</th>
                          <th className="text-center py-2">Qty</th>
                          <th className="text-right py-2">Daily</th>
                          <th className="text-right py-2">Monthly</th>
                          <th className="text-center py-2">Operator</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedSupplier.assets.map((a, i) => (
                          <tr key={i} className="border-b">
                            <td className="py-2">{a.asset_type}</td>
                            <td className="text-center py-2">{a.quantity}</td>
                            <td className="text-right py-2">SAR {a.daily_rate}</td>
                            <td className="text-right py-2">SAR {a.monthly_rate}</td>
                            <td className="text-center py-2">{a.with_operator ? `SAR ${a.operator_rate}/day` : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {selectedSupplier.bank_details && (
                <div>
                  <h4 className="font-semibold mb-2">Bank Details</h4>
                  <div className="p-3 bg-muted rounded-lg text-sm grid grid-cols-2 gap-2">
                    <div><span className="text-muted-foreground">Bank:</span> {selectedSupplier.bank_details.bank_name}</div>
                    <div><span className="text-muted-foreground">Account:</span> {selectedSupplier.bank_details.account_number}</div>
                    <div><span className="text-muted-foreground">IBAN:</span> {selectedSupplier.bank_details.iban || '-'}</div>
                    <div><span className="text-muted-foreground">SWIFT:</span> {selectedSupplier.bank_details.swift_code || '-'}</div>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-muted-foreground">Performance Rating</p>
                  {renderStars(selectedSupplier.performance_rating)}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Orders</p>
                  <p className="font-semibold">{selectedSupplier.total_orders}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Spent</p>
                  <p className="font-semibold">SAR {selectedSupplier.total_spent?.toLocaleString() || 0}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rating Dialog */}
      <Dialog open={isRatingDialogOpen} onOpenChange={setIsRatingDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rate Supplier</DialogTitle>
          </DialogHeader>
          
          {selectedSupplier && (
            <div className="space-y-4">
              <p className="text-muted-foreground">How would you rate {selectedSupplier.name}?</p>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setNewRating(star)}
                    className="p-1 hover:scale-110 transition-transform"
                  >
                    <Star
                      className={`h-8 w-8 ${star <= newRating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
                    />
                  </button>
                ))}
              </div>
              <p className="text-center text-sm text-muted-foreground">
                {newRating === 0 ? 'Click a star to rate' : `${newRating} out of 5 stars`}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRatingDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRate} disabled={newRating === 0}>Submit Rating</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
